import { useRef, type ReactNode } from 'react';
import './Tabs.css';

/**
 * Tabs — 디자인 정본 E5.
 *
 * ⚠ 2026-09-07 이전에는 `BoardTopTabs` 가 「앱에서 유일한 탭 구현」이었는데, 게시판이
 *   좌측 목록으로 갈아타며 **그 화면이 사라졌다**(정본 P1 「A 와 C 를 섞지 않는다」).
 *
 * ★★ **두 모양은 뜻이 다르다.**
 *    · `line` — 화면 상단의 **주 탭**. 「인사기록 / 발령 / 근태 …」처럼 **다른 내용**으로 간다.
 *    · `pill` — **필터 전환**. 「전체 428 / 진행 12 / 승인 380」처럼 **같은 목록을 거른다**.
 *    필터를 line 으로 그리면 사용자가 «다른 화면으로 간다»고 읽는다.
 *
 * ★★ **필터 탭의 숫자는 «그것을 누르면 몇 개 보이나»** 여야 한다. 전체 기준 숫자를 적으면
 *    눌렀을 때 다른 수가 나와 거짓말이 된다(예약 화면에서 실제로 겪었다).
 *
 * ★ 키보드는 ←→ 로 옮기고 Home/End 로 양 끝 — `role="tablist"` 규약이다.
 */
export interface TabItem {
  value: string;
  label: ReactNode;
  /** 필터 탭의 건수 — **모르면 주지 않는다**(0 으로 적지 않는다). */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  items: ReadonlyArray<TabItem>;
  value: string;
  onChange: (value: string) => void;
  variant?: 'line' | 'pill';
  /** 무엇을 고르는 탭인지. 한 화면에 탭이 둘이면 구별돼야 한다. */
  ariaLabel: string;
  className?: string;
}

export function Tabs({ items, value, onChange, variant = 'line', ariaLabel, className }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const focusAt = (idx: number) => {
    const tabs = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])');
    if (!tabs || tabs.length === 0) return;
    const i = ((idx % tabs.length) + tabs.length) % tabs.length;
    tabs[i]?.focus();
    tabs[i]?.click();
  };

  const onKeyDown = (e: React.KeyboardEvent, idx: number) => {
    const map: Record<string, () => void> = {
      ArrowLeft: () => focusAt(idx - 1),
      ArrowRight: () => focusAt(idx + 1),
      Home: () => focusAt(0),
      End: () => focusAt(items.length - 1),
    };
    const fn = map[e.key];
    if (fn) { e.preventDefault(); fn(); }
  };

  return (
    <div
      ref={listRef}
      className={['wc-tabs', `wc-tabs--${variant}`, className ?? ''].filter(Boolean).join(' ')}
      role="tablist"
      aria-label={ariaLabel}
    >
      {items.map((it, i) => {
        const on = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={on}
            className={`wc-tabs__tab${on ? ' is-on' : ''}`}
            disabled={it.disabled}
            /* 고른 것만 tab 으로 들어온다 — 탭 목록 전체를 하나씩 지나가게 하지 않는다. */
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(it.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            <span className="wc-tabs__label">{it.label}</span>
            {it.count != null && <span className="wc-tabs__count">{it.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
